import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import AddBoxIcon from '@mui/icons-material/AddBox';
import {
    usePortfolioSelection,
    usePortfolioManagement,
} from "../../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { useState } from "react";
import { PortfolioAddOrUpdateModel } from "../../types/PortfolioAddOrUpdateModel";
import { PortfolioManagerDialog } from "../../components/PortfolioManagerDialog";
import {
    ActionConfirmationDialog
} from "../../../../shared/components/action-confirmation-dialog/ActionConfirmationDialog";
import {
    useActionConfirmationDialog
} from "../../../../shared/components/action-confirmation-dialog/useActionConfirmationDialog";
import { PortfolioSelector } from "../../components/PortfolioSelector";
import { PortfolioModel } from "../../types/PortfolioModel";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { PortfolioGroupSelector } from "../../components/PortfolioGroupSelector";
import { PortfolioGroupModel } from "../../types/PortfolioGroupModel";
import { PortfolioGroupManagerDialog } from "../../components/PortfolioGroupManagerDialog";
import { PortfolioGroupAddOrUpdateModel } from "../../types/PortfolioGroupAddOrUpdateModel";

export function PortfolioSelectorView() {
    const {
        selectedPortfolio,
        portfolioGroups,
        selectedPortfolioGroup,
        portfolios,
        setSelectedPortfolioByName,
        setSelectedPortfolioGroupByName,
        setAllPortfoliosByUnderlyingAsset,
        isAllPortfoliosSelected,
    } = usePortfolioSelection();
    const {
        createPortfolioGroup,
        editSelectedPortfolioGroup,
        createPortfolio,
        editSelectedPortfolio,
        saveSelectedPortfolio,
        cloneSelectedPortfolio,
    } = usePortfolioManagement();

    const {
        isActionConfirmationDialogOpen,
        setIsActionConfirmationDialogOpen,
        confirmedAction,
        setConfirmedAction,
        confirmationMessage,
        setConfirmationMessage,
        submitButtonName,
        setSubmitButtonName,
    } = useActionConfirmationDialog();
    
    const [isPortfolioManagerDialogOpen, setIsPortfolioManagerDialogOpen] = useState<boolean>(false);
    const [isPortfolioGroupManagerDialogOpen, setIsPortfolioGroupManagerDialogOpen] = useState<boolean>(false);
    const [isCreation, setIsCreation] = useState<boolean>(false);
    const [isPortfolioGroupCreation, setIsPortfolioGroupCreation] = useState<boolean>(false);
    const [submitAction, setSubmitAction] = useState<(portfolioModel: PortfolioAddOrUpdateModel) => void | Promise<void>>();
    const [portfolioGroupSubmitAction, setPortfolioGroupSubmitAction] = useState<(portfolioGroupModel: PortfolioGroupAddOrUpdateModel) => void>();
    const [editingPortfolioModel, setEditingPortfolioModel] = useState<PortfolioAddOrUpdateModel | null>(null);
    const [editingPortfolioGroupModel, setEditingPortfolioGroupModel] = useState<PortfolioGroupAddOrUpdateModel | null>(null);

    const handlePortfolioGroupCreate = () => {
        setIsPortfolioGroupCreation(true);
        setPortfolioGroupSubmitAction(() => async (portfolioGroupAddModel: PortfolioGroupAddOrUpdateModel) => {
            await createPortfolioGroup(portfolioGroupAddModel);
        });
        setIsPortfolioGroupManagerDialogOpen(true);
    };

    const handlePortfolioGroupUpdate = () => {
        if (!selectedPortfolioGroup) {
            return;
        }

        setIsPortfolioGroupCreation(false);
        setEditingPortfolioGroupModel({
           name: selectedPortfolioGroup.name,
           usePopupForStepChangeSelector:
               selectedPortfolioGroup.portfolioSettings?.usePopupForStepChangeSelector ?? false,
        });
        setPortfolioGroupSubmitAction(() => async (portfolioGroupUpdateModel: PortfolioGroupAddOrUpdateModel) => {
            await editSelectedPortfolioGroup(portfolioGroupUpdateModel);
        });
        setIsPortfolioGroupManagerDialogOpen(true);
    };

    const handlePortfolioCreate = () => {
        setIsCreation(true);
        setSubmitAction(() => async (portfolioAddModel: PortfolioAddOrUpdateModel) => {
            await createPortfolio(portfolioAddModel);
        });
        setIsPortfolioManagerDialogOpen(true);
    };

    const handlePortfolioUpdate = () => {
        if (!selectedPortfolio) {
            return;
        }
        
        setIsCreation(false);
        setEditingPortfolioModel({
           name: selectedPortfolio.name,
           description: selectedPortfolio.description,
           marginBuffer: selectedPortfolio.marginBuffer,
           inceptionDate: selectedPortfolio.inceptionDate,
           usePopupForStepChangeSelector:
               selectedPortfolio.portfolioSettings?.usePopupForStepChangeSelector ?? false,
           useValuationDate: selectedPortfolio.useValuationDate,
           valuationDate: selectedPortfolio.valuationDate,
           portfolioGroup: selectedPortfolio.portfolioGroup,
        });
        setSubmitAction(() => async (portfolioUpdateModel: PortfolioAddOrUpdateModel) => {
            await editSelectedPortfolio(portfolioUpdateModel);
        });
        setIsPortfolioManagerDialogOpen(true);
    };

    const handleSave = () => {
        setConfirmedAction(() => saveSelectedPortfolio);
        setConfirmationMessage("Are you sure you want to save the portfolio?");
        setSubmitButtonName("Save");
        setIsActionConfirmationDialogOpen(true);
    }

    const handleClone = () => {
        setConfirmedAction(() => cloneSelectedPortfolio);
        setConfirmationMessage("Are you sure you want to clone the portfolio?");
        setSubmitButtonName("Clone");
        setIsActionConfirmationDialogOpen(true);
    }
    
    const primaryColour = "#FACC2D";
    const secondaryColour = "#FACC2D";
    const secondaryHoverColour = "#FFFFFF";
    const disabledColour = "#c5c5c5";
    
    const actionIconSize = 36;
    
    return (
        <>
            <ActionConfirmationDialog
                isOpen={isActionConfirmationDialogOpen}
                setIsOpen={setIsActionConfirmationDialogOpen}
                confirmationMessage={confirmationMessage}
                submitButtonName={submitButtonName}
                confirmedParentAction={confirmedAction}
            />
            <Stack
                direction="row"
                spacing={1}
                alignItems="flex-end"
            >
                <PortfolioManagerDialog
                    isOpen={isPortfolioManagerDialogOpen}
                    setIsOpen={setIsPortfolioManagerDialogOpen}
                    isCreation={isCreation}
                    submitAction={submitAction}
                    editingPortfolioModel={editingPortfolioModel}
                    icon={isCreation ? <AddBoxIcon /> : <EditIcon />}
                />
                <PortfolioGroupManagerDialog
                    isOpen={isPortfolioGroupManagerDialogOpen}
                    setIsOpen={setIsPortfolioGroupManagerDialogOpen}
                    isCreation={isPortfolioGroupCreation}
                    submitAction={portfolioGroupSubmitAction}
                    editingPortfolioGroupModel={editingPortfolioGroupModel}
                    icon={isPortfolioGroupCreation ? <AddBoxIcon /> : <EditIcon />}
                />
                <Box>
                    <Tooltip
                        title="Create a new portfolio group"
                        slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                    >
                        <IconButton
                            sx={{
                                color: primaryColour,
                                padding: '0px',
                                '&:hover': {
                                    color: secondaryHoverColour,
                                },
                            }}
                            onClick={handlePortfolioGroupCreate}
                        >
                            <AddBoxIcon sx={{ fontSize: actionIconSize }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title="Edit the portfolio group"
                        slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                    >
                        <IconButton
                            sx={{
                                color: primaryColour,
                                padding: '0px',
                                '&:hover': {
                                    color: secondaryHoverColour,
                                },
                            }}
                            onClick={handlePortfolioGroupUpdate}
                            disabled={!selectedPortfolioGroup}
                        >
                            <EditIcon sx={{ fontSize: actionIconSize }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <PortfolioGroupSelector
                    portfolioGroups={portfolioGroups}
                    selectedPortfolioGroup={selectedPortfolioGroup}
                    setSelectedPortfolioGroup={setSelectedPortfolioGroupByName}
                    labelText="Portfolio groups"
                    primaryColour={primaryColour}
                    secondaryColour={secondaryColour}
                    secondaryHoverColour={secondaryHoverColour}
                    disabled={!isAnyPortfolioGroupAvailable(portfolioGroups)}
                />
                <Box sx={{ pl: 2 }}>
                    <Tooltip
                        title="Create a new portfolio"
                        slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                    >
                        <IconButton
                            sx={{ 
                                color: primaryColour,
                                padding: '0px',
                                '&:hover': {
                                    color: secondaryHoverColour,
                                },
                            }}
                            onClick={handlePortfolioCreate}
                        >
                            <AddBoxIcon sx={{ fontSize: actionIconSize }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title="Edit the portfolio"
                        slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                    >
                        <IconButton
                            sx={{
                                color: primaryColour,
                                padding: '0px',
                                '&:hover': {
                                    color: secondaryHoverColour,
                                },
                            }}
                            onClick={handlePortfolioUpdate}
                            disabled={isAllPortfoliosSelected}
                        >
                            <EditIcon sx={{ fontSize: actionIconSize }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title="Clone the portfolio"
                        slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                    >
                        <IconButton
                            sx={{
                                color: primaryColour,
                                padding: '0px',
                                '&:hover': {
                                    color: secondaryHoverColour,
                                },
                            }}
                            onClick={handleClone}
                            disabled={isAllPortfoliosSelected}
                        >
                            <ContentCopyIcon sx={{ fontSize: actionIconSize }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Tooltip title={selectedPortfolio?.description}>
                    <PortfolioSelector
                        portfolios={portfolios}
                        selectedPortfolio={selectedPortfolio}
                        setSelectedPortfolio={setSelectedPortfolioByName}
                        setAllPortfolios={setAllPortfoliosByUnderlyingAsset}
                        labelText="Portfolios"
                        primaryColour={primaryColour}
                        secondaryColour={secondaryColour}
                        secondaryHoverColour={secondaryHoverColour}
                        disabled={!isAnyPortfolioAvailable(portfolios)}
                    />
                </Tooltip>
                                
                {/*{selectedPortfolio ? (*/}
                {/*    <TextField*/}
                {/*        id="standard-multiline-static"*/}
                {/*        label="Description"*/}
                {/*        multiline*/}
                {/*        // disabled*/}
                {/*        rows={1}*/}
                {/*        value={selectedPortfolio.description}*/}
                {/*        variant="standard"*/}
                {/*        size="small"*/}
                {/*        aria-readonly*/}
                {/*        sx={{*/}
                {/*            marginLeft: "10px",*/}
                {/*            width: "500px",*/}
                {/*            // color: secondaryColour,*/}
                {/*            // "&.Mui-focused": {*/}
                {/*            //     color: secondaryHoverColour*/}
                {/*            // }*/}
                
                {/*            // color: primaryColour,*/}
                {/*            // '&:before': {*/}
                {/*            //     borderColor: secondaryColour,*/}
                {/*            // },*/}
                {/*            // '&:after': {*/}
                {/*            //     borderColor: secondaryHoverColour,*/}
                {/*            // },*/}
                {/*            // '&:not(.Mui-disabled):hover::before': {*/}
                {/*            //     borderColor: secondaryColour,*/}
                {/*            // },*/}
                {/*            // '.MuiSvgIcon-root': {*/}
                {/*            //     fill: `${secondaryColour} !important`,*/}
                {/*            // }*/}
                {/*            */}
                {/*            // Root class for the input field*/}
                {/*            '&.MuiStandardInput-root': {*/}
                {/*                color: primaryColour,*/}
                {/*                fontFamily: "Arial",*/}
                {/*                fontWeight: "bold",*/}
                {/*            // Class for the border around the input field*/}
                {/*            '&.MuiStandardInput-notchedStandard': {*/}
                {/*                    borderColor: primaryColour,*/}
                {/*                    borderWidth: "2px",*/}
                {/*                },*/}
                {/*            },*/}
                {/*            // Class for the label of the input field*/}
                {/*            '&.MuiInputLabel-outlined': {*/}
                {/*                color: primaryColour,*/}
                {/*                fontWeight: "bold",*/}
                {/*            },*/}
                {/*        }}*/}
                {/*    />*/}
                {/*) : (*/}
                {/*    <></>*/}
                {/*)}*/}
            </Stack>
        </>
    );
}

function isAnyPortfolioAvailable(portfolios: PortfolioModel[]): boolean {
    return portfolios
        ? portfolios.length != 0
        : false;
}

function isAnyPortfolioGroupAvailable(portfolioGroups: PortfolioGroupModel[]): boolean {
    return portfolioGroups
        ? portfolioGroups.length !== 0
        : false;
}
